import React from 'react';
import { formatDate } from '../../utils/dateFormatter';

export const useDeliveryNotePrint = () => {
  const handlePrint = (note) => {
    const printWindow = window.open('', '_blank');

    const hasIncomplete = (note.items || []).some(item => (item.ruined_quantity || 0) > 0);

    // Group items by order_id (fall back to a single group if old-style note)
    const orderGroups = {};
    (note.items || []).forEach(item => {
      const key = item.order_id || '__legacy__';
      if (!orderGroups[key]) {
        orderGroups[key] = {
          order_number: item.order_number || note.order_id || '',
          item_code: item.item_code || '',
          card_number: item.card_number || '',
          procurement_date: item.procurement_date || '',
          production_date: item.production_date || '',
          items: [],
        };
      }
      orderGroups[key].items.push(item);
    });

    const buildOrderSection = (group, globalIdx) => {
      let rowsHtml = '';
      let localIdx = globalIdx;
      group.items.forEach(item => {
        rowsHtml += `
          <tr>
            <td style="border: 1px solid #333; padding: 8px;">${localIdx++}</td>
            <td style="border: 1px solid #333; padding: 8px;">${item.description}</td>
            <td style="border: 1px solid #333; padding: 8px; text-align: center;">${item.quantity}</td>
            ${hasIncomplete ? `<td style="border: 1px solid #333; padding: 8px; text-align: center; color:#c00;">${item.ruined_quantity || 0}</td>` : ''}
          </tr>`;
      });

      const metaRows = [
        group.order_number ? `<div class="info-item"><span class="info-label">Order Ref:</span> #${group.order_number}</div>` : '',
        group.item_code ? `<div class="info-item"><span class="info-label">Item Code:</span> ${group.item_code}</div>` : '',
        group.card_number ? `<div class="info-item"><span class="info-label">Card No:</span> ${group.card_number}</div>` : '',
        group.procurement_date ? `<div class="info-item"><span class="info-label">Procurement Date:</span> ${group.procurement_date}</div>` : '',
        group.production_date ? `<div class="info-item"><span class="info-label">Production Date:</span> ${group.production_date}</div>` : '',
      ].filter(Boolean).join('');

      return { html: rowsHtml, nextIdx: localIdx, metaRows };
    };

    const keys = Object.keys(orderGroups);
    const multiOrder = keys.length > 1;

    let allItemsHtml = '';
    let globalIdx = 1;

    if (multiOrder) {
      // Multi-order: render a section per order
      allItemsHtml = keys.map(key => {
        const group = orderGroups[key];
        const { html, nextIdx, metaRows } = buildOrderSection(group, globalIdx);
        globalIdx = nextIdx;
        return `
          <div class="order-section">
            <div class="order-meta">${metaRows}</div>
            <table>
              <thead>
                <tr>
                  <th style="width:50px;">#</th>
                  <th>Description</th>
                  <th style="width:100px; text-align:center;">Quantity</th>
                  ${hasIncomplete ? '<th style="width:100px; text-align:center; color:#c00;">Incomplete</th>' : ''}
                </tr>
              </thead>
              <tbody>${html}</tbody>
            </table>
          </div>`;
      }).join('');
    } else {
      // Single order (or legacy): one flat table
      const group = orderGroups[keys[0]] || { items: note.items || [], order_number: note.order_id || '', item_code: '', card_number: '', procurement_date: '', production_date: '' };
      const { html } = buildOrderSection(group, 1);
      allItemsHtml = `
        <table>
          <thead>
            <tr>
              <th style="width:50px;">#</th>
              <th>Description</th>
              <th style="width:100px; text-align:center;">Quantity</th>
              ${hasIncomplete ? '<th style="width:100px; text-align:center; color:#c00;">Incomplete</th>' : ''}
            </tr>
          </thead>
          <tbody>${html}</tbody>
        </table>`;
    }

    // Build order info block for single-order notes
    const singleGroup = !multiOrder ? (orderGroups[keys[0]] || {}) : null;
    const orderRefHtml = !multiOrder && singleGroup ? [
      singleGroup.order_number ? `<div class="info-item"><span class="info-label">Order Reference:</span> #${singleGroup.order_number}</div>` : '',
      singleGroup.item_code ? `<div class="info-item"><span class="info-label">Item Code:</span> ${singleGroup.item_code}</div>` : '',
      singleGroup.card_number ? `<div class="info-item"><span class="info-label">Card No:</span> ${singleGroup.card_number}</div>` : '',
      singleGroup.procurement_date ? `<div class="info-item"><span class="info-label">Procurement Date:</span> ${singleGroup.procurement_date}</div>` : '',
      singleGroup.production_date ? `<div class="info-item"><span class="info-label">Production Date:</span> ${singleGroup.production_date}</div>` : '',
    ].filter(Boolean).join('') : '';

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Delivery Note ${note.note_number}</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { font-family: Arial, sans-serif; padding: 30px; color: #000; background: #fff; }
            .container { max-width: 800px; margin: 0 auto; }
            .header { text-align: center; margin-bottom: 30px; padding-bottom: 15px; border-bottom: 2px solid #333; }
            .header h1 { font-size: 22pt; margin-bottom: 5px; }
            .header p { font-size: 10pt; color: #666; }
            .doc-title { text-align: center; margin-bottom: 25px; }
            .doc-title h2 { font-size: 16pt; text-transform: uppercase; }
            .doc-title p { font-size: 14pt; margin-top: 5px; }
            .info-grid { display: flex; justify-content: space-between; margin-bottom: 25px; }
            .info-left, .info-right { width: 48%; }
            .info-right { text-align: right; }
            .info-item { margin-bottom: 8px; }
            .info-label { font-weight: bold; }
            table { width: 100%; border-collapse: collapse; margin-bottom: 25px; }
            th { background-color: #f0f0f0; border: 1px solid #333; padding: 10px; text-align: left; font-weight: bold; }
            td { border: 1px solid #333; padding: 8px; }
            .order-section { margin-bottom: 30px; }
            .order-meta { background: #f5f5f5; border: 1px solid #ddd; padding: 10px 12px; margin-bottom: 8px; border-radius: 4px; font-size: 10pt; display: flex; flex-wrap: wrap; gap: 20px; }
            .order-meta .info-item { margin-bottom: 0; }
            .notes-section { margin-top: 20px; padding: 15px; background: #f9f9f9; border: 1px solid #ddd; }
            .notes-section h4 { margin-bottom: 8px; }
            .signatures { display: flex; justify-content: space-between; margin-top: 60px; }
            .sig-line { width: 200px; border-top: 1px solid #333; padding-top: 5px; text-align: center; font-size: 10pt; }
            .footer { margin-top: 50px; padding-top: 15px; border-top: 1px solid #ccc; text-align: center; font-size: 9pt; color: #666; }
          </style>
        </head>
        <body>
          <div class="container">
            <div class="header">
              <h1>CLADAKIS FOR READY MADE GARMENTS</h1>
              <p>Garment Manufacturing &amp; Trading</p>
            </div>
            <div class="doc-title">
              <h2>Delivery Note</h2>
              <p>${note.note_number}</p>
            </div>
            <div class="info-grid">
              <div class="info-left">
                <div class="info-item"><span class="info-label">Client:</span> ${note.client_name}</div>
                ${orderRefHtml}
              </div>
              <div class="info-right">
                <div class="info-item"><span class="info-label">Delivery Date:</span> ${note.delivery_date}</div>
                <div class="info-item"><span class="info-label">Note No:</span> ${note.note_number}</div>
              </div>
            </div>
            ${allItemsHtml}
            ${note.notes ? `
            <div class="notes-section">
              <h4>Additional Notes:</h4>
              <p>${note.notes}</p>
            </div>` : ''}
            <div class="signatures">
              <div class="sig-line">Delivered By</div>
              <div class="sig-line">Received By</div>
            </div>
            <div class="footer">
              <p>Goods received in good condition</p>
              <p>Printed on: ${formatDate(new Date())}</p>
            </div>
          </div>
          <script>
            window.onload = function() { window.print(); window.onafterprint = function() { window.close(); } }
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  return { handlePrint };
};
